import { AppSettings } from "@/AppSettings";
import { IHasAudit } from "@/api/dtos";
import Field from "@/components/forms/fields/Field";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Select from "@/components/forms/inputs/Select";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Checkbox, Divider, IconButton, List, ListItem, ListItemText, Paper, Tooltip } from "@mui/material";
import { useFormActions, useScopedField } from "@ngt/forms-core";
import { useSnackbar } from "notistack";
import { FC, Fragment, memo, useEffect, useMemo, useState } from "react";

export enum ConfigurationSourceDataBulkEditFieldMode {
    FormEdit = 1,
    ListEdit = 2,
    BulkEdit = 3
}

export interface IConfigurationSourceDataBulkEditFieldProps<TData extends IConfigurationSourceDataBulkEditData> {
    name: string;
    description?: string;
    propertyPath: string;
    margin?: 'dense' | 'normal' | 'none';
    disabled?: boolean;
    data?: TData;
}

export interface IConfigurationSourceDataBulkEditField<TData extends IConfigurationSourceDataBulkEditData> {
    propertyPath: string;
    name: string;
    description?: string;
    Component: FC<IConfigurationSourceDataBulkEditFieldProps<TData>>;
}

export interface IConfigurationSourceDataBulkEditSettings<TConfigurationModel extends IConfigurationSourceDataBulkEditConfiguration, TData extends IConfigurationSourceDataBulkEditData> {
    mapping: Record<keyof TConfigurationModel, IConfigurationSourceDataBulkEditMapping<TData> | undefined>;
    getLocalConfigurationPropertyPath: (data: TData) => string;
    getParentConfigurationPropertyPath: (data: TData) => string;
    getKey: (data: TData) => string;
    copyConfiguration: (source: TConfigurationModel, destination: TConfigurationModel) => TConfigurationModel;
    createNewConfiguration: (data: TData) => TConfigurationModel;
}

export interface IConfigurationSourceDataBulkEditMapping<TData extends IConfigurationSourceDataBulkEditData> {
    name: string,
    description?: string,
    required: boolean;
    component: FC<TData>;
    bulkEditComponent?: FC;
}

export interface IConfigurationSourceDataBulkEditConfiguration extends IHasAudit {
    linked?: boolean;
}

export interface IConfigurationSourceDataBulkEditData {
    displayName?: string;
    icon: IconProp;
    linked?: boolean;
}

export interface IConfigurationSourceDataBulkEditProps<TConfigurationModel extends IConfigurationSourceDataBulkEditConfiguration, TData extends IConfigurationSourceDataBulkEditData> {
    data: TData[];
    settings: IConfigurationSourceDataBulkEditSettings<TConfigurationModel, TData>;
    onLinkUpdate?: () => void;
    onCancel?: () => void;
}

const ConfigurationSourceDataBulkEdit = <TConfigurationModel extends IConfigurationSourceDataBulkEditConfiguration, TData extends IConfigurationSourceDataBulkEditData>({
    data,
    settings,
    onLinkUpdate,
    onCancel
}: IConfigurationSourceDataBulkEditProps<TConfigurationModel, TData>) => {
    const { enqueueSnackbar } = useSnackbar();

    const {
        mapping,
        getLocalConfigurationPropertyPath,
        getParentConfigurationPropertyPath,
        getKey,
        copyConfiguration,
        createNewConfiguration
    } = settings;

    const properties = useMemo(() => {
        return Object.keys(mapping)
            .filter(x => (mapping as any)[x as any] !== undefined)
            .map(x => {
                const value = (mapping as any)[x as any];

                return {
                    id: x,
                    name: value.name,
                    description: value.description
                }
            })
    }, [mapping])

    useEffect(() => {
        setFieldValue('BULK_EDIT.property', properties.find(_ => true)?.id, true, false, false, true);
    }, [properties])
    
    const { setFieldValue, getFieldValue } = useFormActions();

    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const { state: { value: selectedProperty } } = useScopedField<keyof TConfigurationModel>('BULK_EDIT.property', { value: true });

    const FieldComponent = selectedProperty ? mapping[selectedProperty]?.component : undefined;
    const BulkFieldComponent = selectedProperty ? mapping[selectedProperty]?.bulkEditComponent : undefined;

    const selectedMap = useMemo(() => {
        const map: Record<string, boolean> = {}

        for (let key of selectedItems) {
            map[key] = true;
        }

        return map;
    }, [selectedItems])

    const allSelected = selectedItems.length === data.length;
    const someSelected = selectedItems.length < data.length && selectedItems.length !== 0;

    return (
        <>
            <Box
                sx={{
                    my: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center'
                }}
            >
                
                <Box
                    sx={{
                        ml: 2
                    }}
                >
                    <Tooltip 
                        title={
                            allSelected ?
                                'Unselect All' :
                                'Select All'
                        }
                    >
                        <Checkbox 
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(_, checked) => {
                                if (selectedItems.length === 0) {
                                    setFieldValue('BULK_EDIT.value', undefined, true, false, false, true);
                                }

                                if (selectedItems.length < data.length) {
                                    setSelectedItems(data.map(getKey))
                                }
                                else {
                                    setSelectedItems([])
                                }
                            }}
                        />
                    </Tooltip>
                </Box>
                <InputOnlyField
                    propertyPath="BULK_EDIT.property"
                    label="Property"
                    FormControlProps={{
                        margin: 'none'
                    }}
                    nullOption={false}
                    component={Select}
                    options={properties}
                />
                <Button
                    variant="contained"
                    color="primary"
                    sx={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        minHeight: 52
                    }}
                    onClick={onCancel}
                >
                    Bulk Edit
                </Button>
            </Box> 
            <Paper>
                <List disablePadding>
                    {
                        data.map((item, index, array) => {
                            const localConfigurationPropertyPath = getLocalConfigurationPropertyPath(item);
                            const parentConfigurationPropertyPath = getParentConfigurationPropertyPath(item);
                            const key = getKey(item);

                            return (
                                <Fragment
                                    key={key}
                                >
                                    <Field
                                        propertyPath={item.linked ? 
                                            parentConfigurationPropertyPath :
                                            localConfigurationPropertyPath
                                        }
                                    >
                                        <ListItem
                                            sx={{
                                                gap: 4,
                                                py: 2
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    maxWidth: '50%',
                                                    minWidth: '50%',
                                                    gap: 2
                                                }}
                                            >
                                                <Box>
                                                    <Checkbox 
                                                        checked={!!selectedMap[key]}
                                                        onChange={(_, checked) => {
                                                            if (selectedItems.length === 0) {
                                                                setFieldValue('BULK_EDIT.value', undefined, true, false, false, true);
                                                            }

                                                            if (checked) {
                                                                setSelectedItems(current => {
                                                                    return [...current, key]
                                                                })
                                                            }
                                                            else {
                                                                setSelectedItems(current => {
                                                                    return current.filter(y => y !== key)
                                                                })
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                                <Box>
                                                    <FontAwesomeIcon fixedWidth size="lg" icon={item.icon} />
                                                </Box>
                                                <ListItemText 
                                                    primary={item.displayName} 
                                                />
                                                {
                                                    item.linked ?
                                                        <IconButton
                                                            onClick={() => {
                                                                setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                                                    currentValue.linked = false;
                                                        
                                                                    const linkedValue = getFieldValue<TConfigurationModel>(parentConfigurationPropertyPath);
                                                        
                                                                    if (linkedValue) {
                                                                        return copyConfiguration(linkedValue, currentValue);
                                                                    }
                                                        
                                                                    return currentValue;
                                                                }, true, true, true);

                                                                onLinkUpdate?.();
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={AppSettings.icons.link} />
                                                        </IconButton> :
                                                        <IconButton
                                                            onClick={() => {
                                                                setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                                                    currentValue.linked = true;
                                                                    return copyConfiguration(createNewConfiguration(item), currentValue)
                                                                }, true, true, true);

                                                                onLinkUpdate?.();
                                                            }} 
                                                        >
                                                            <FontAwesomeIcon icon={AppSettings.icons.unlink} />
                                                        </IconButton>
                                                }
                                            </Box>
                                            <Box
                                                sx={{
                                                    flex: '1 1 auto'
                                                }}
                                            >
                                                {
                                                    FieldComponent && (
                                                        <FieldComponent {...item} />
                                                    )
                                                }
                                            </Box>
                                        </ListItem>
                                        {(index + 1) < array.length && <Divider />}
                                        
                                    </Field>
                                </Fragment>
                            )
                        })
                    }
                </List>
            </Paper>
            {
                selectedItems.length > 0 && BulkFieldComponent && (
                    <Paper
                        
                        sx={{
                            borderRadius: 0,
                            position: 'sticky',
                            bottom: 0,
                            mx: -3,
                            mt: 3,
                            mb: -3,
                            padding: 3,
                            zIndex: 1,
                            display: 'flex',
                            gap: 4,
                            alignItems: 'center'
                        }}
                    >
                        <Box
                            sx={{ 
                                minWdith: 500,
                                width: 500,
                                maxWidth: 500
                            }}
                        >
                            Update {selectedItems.length} Item(s)
                        </Box>
                        <Field
                            propertyPath="BULK_EDIT"
                        >
                            <BulkFieldComponent />
                        </Field>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                const value = getFieldValue('BULK_EDIT.value');

                                if (mapping[selectedProperty]?.required === true && (value === null || value === undefined)) {
                                    enqueueSnackbar(`This field must have a value!`, { variant: "error" });
                                    return;
                                } 

                                for (let selectedKey of selectedItems) {
                                    const item = data.find(x => getKey(x) === selectedKey);

                                    if (!item) {
                                        continue;
                                    }

                                    const localConfigurationPropertyPath = getLocalConfigurationPropertyPath(item);

                                    if (item?.linked) {
                                        const parentConfigurationPropertyPath = getParentConfigurationPropertyPath(item);

                                        setFieldValue<TConfigurationModel>(localConfigurationPropertyPath, currentValue => {
                                            currentValue.linked = false;
                                
                                            const linkedValue = getFieldValue<TConfigurationModel>(parentConfigurationPropertyPath);
                                
                                            if (linkedValue) {
                                                return copyConfiguration(linkedValue, currentValue);
                                            }
                                
                                            return currentValue;
                                        }, true, true, true);

                                        onLinkUpdate?.();
                                    }

                                    setFieldValue(`${localConfigurationPropertyPath}.${selectedProperty as string}`, value);
                                }
                            }}
                        >
                            Update
                        </Button>
                    </Paper>
                )
            }
        </>
    )
}

export default memo(ConfigurationSourceDataBulkEdit) as typeof ConfigurationSourceDataBulkEdit;